import { useCallback, useEffect, useState } from "react";
import { FindAllCandidatesFilter, PaginationType } from "../Interface";
import { UserWithProfile } from "../Entities/User";
import userService from "../Service/user.service";

const useTalentPool = (
  tab:
    | "all"
    | "resume-submissions"
    | "applied-by-candidate"
    | "applied-by-recruiter",
) => {
  const defaultFilters: FindAllCandidatesFilter = {
    normalSearch: null,
    querySearch: null,
    sort: null,
    source:
      tab === "resume-submissions"
        ? "direct_upload"
        : tab === "applied-by-candidate"
          ? "job_application"
          : tab === "applied-by-recruiter"
            ? "bulk_upload"
            : null,
  };

  const defaultPagination = (): PaginationType => ({
    page: 1,
    limit: 10,
    totalRecords: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [filters, setFilters] =
    useState<FindAllCandidatesFilter>(defaultFilters);
  const [pagination, setPagination] =
    useState<PaginationType>(defaultPagination);
  const [users, setUsers] = useState<UserWithProfile[]>([]);

  const updatePagination = (page: number) => {
    setPagination((prev) => ({
      ...prev,
      page: page,
    }));
  };

  const updateFilters = (key: keyof FindAllCandidatesFilter, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
  };

  const clearFilter = () => {
    setFilters(defaultFilters);
    setPagination(defaultPagination);
  };

  const { page, limit } = pagination;
  const { sort, source, querySearch, normalSearch } = filters;

  const getTalentPool = useCallback(async () => {
    try {
      setLoading(true);

      const talentPoolResponse = await userService.getAllUsersWithProfile(
        {
          sort,
          source,
          querySearch,
          normalSearch,
        },
        page,
        limit,
      );

      if (talentPoolResponse) {
        setUsers(talentPoolResponse.data || []);
        setPagination((prev) => ({
          ...prev,
          totalRecords: talentPoolResponse.total || 0,
          totalPages: talentPoolResponse.totalPages || 0,
        }));
      } else {
        console.error(
          "~ useTalentPool ~ Inavlid talentPoolResponse:",
          talentPoolResponse,
        );
        setUsers([]);
        setPagination((prev) => ({
          ...prev,
          totalRecords: 0,
          totalPages: 0,
        }));
      }
    } catch (error) {
      console.error("~ useTalentPool - getTalentPool ~ error:", error);
      setUsers([]);
      setPagination((prev) => ({
        ...prev,
        totalRecords: 0,
        totalPages: 0,
      }));
    } finally {
      setLoading(false);
    }
  }, [page, limit, sort, source, querySearch, normalSearch, setLoading]);

  useEffect(() => {
    getTalentPool();
  }, [getTalentPool]);

  return {
    loading,
    filters,
    pagination,
    users,
    updatePagination,
    updateFilters,
    clearFilter,
  };
};

export default useTalentPool;
